import { Link } from '@/components/Link';
import { LaunchBadges } from './LaunchBadges';
import { TOOLS } from '@/data/toolCatalog';
import { BrandGlyph } from '../components/BrandIcon';
import { BrandMark } from '../components/BrandMark';
import { DiscordIcon } from '../components/DiscordIcon';
import { Icon } from '../components/Icon';
import { DISCORD_URL } from '../lib/brand';
import { m } from '../paraglide/messages';

export interface FooterProps {
  variant?: 'full' | 'slim';
}

/** Public profiles (entity sameAs). Linked here so the homepage exposes them. */
const SOCIALS: Array<{ label: string; href: string; brand: string }> = [
  { label: 'GitHub', href: 'https://github.com/getanyapi-com', brand: 'github' },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/143034716/',
    brand: 'linkedin',
  },
  { label: 'X', href: 'https://x.com/getanyapi', brand: 'x-twitter' },
];

interface FooterLink {
  label: string;
  to: string;
  external?: boolean;
}

interface FooterColumn {
  title: string;
  /** A continuation column repeats the title as a spacer so its links line up
   *  with the ones beside them without claiming a second heading. */
  continuation?: boolean;
  links: FooterLink[];
}

// Tool titles stay English: they name English pages, and each is the product
// name its page ranks for.
const toolLinks = (): FooterLink[] => [
  { label: m.footer_all_free_tools(), to: '/tools' },
  ...TOOLS.map((t) => ({ label: t.title, to: t.to })),
];

/** Ten links is as long as a column gets before it reads as a list to scroll. */
const TOOL_COLUMN_MAX = 10;

const toolColumns = (): FooterColumn[] => {
  const links = toolLinks();
  const columns: FooterColumn[] = [];
  for (let i = 0; i < links.length; i += TOOL_COLUMN_MAX) {
    columns.push({
      title: m.footer_free_tools(),
      continuation: i > 0,
      links: links.slice(i, i + TOOL_COLUMN_MAX),
    });
  }
  return columns;
};

/** A function so every label is read in the locale of the render. */
const footerColumns = (): FooterColumn[] => [
  {
    title: m.footer_product(),
    links: [
      { label: m.nav_api_prices(), to: '/catalog' },
      { label: m.footer_providers(), to: '/providers' },
      { label: m.footer_alternatives(), to: '/alternatives' },
      { label: m.footer_dashboard(), to: '/dashboard' },
    ],
  },
  {
    title: m.footer_developers(),
    links: [
      { label: m.nav_docs(), to: 'https://getanyapi.com/docs', external: true },
      { label: m.footer_quickstart(), to: 'https://getanyapi.com/docs/quickstart', external: true },
      { label: 'MCP', to: 'https://getanyapi.com/docs/mcp-server', external: true },
      { label: m.footer_ai_agents(), to: 'https://getanyapi.com/SKILL.md', external: true },
    ],
  },
  {
    title: m.footer_company(),
    links: [
      { label: m.footer_about(), to: '/about' },
      { label: m.footer_blog(), to: '/blog' },
      { label: m.nav_affiliates(), to: '/affiliates' },
      { label: m.footer_contact(), to: '/contact' },
      { label: m.footer_security(), to: '/security' },
      { label: 'Discord', to: DISCORD_URL, external: true },
    ],
  },
  {
    title: m.footer_legal(),
    links: [
      { label: m.footer_terms(), to: '/terms' },
      { label: m.footer_privacy(), to: '/privacy' },
      { label: m.footer_acceptable_use(), to: '/acceptable-use' },
      { label: m.footer_data_processing(), to: '/dpa' },
      { label: m.footer_affiliate_terms(), to: '/affiliate-terms' },
      { label: m.footer_seller_terms(), to: '/seller-terms' },
      { label: m.footer_sellers(), to: '/sellers' },
    ],
  },
  ...toolColumns(),
];

const LINK_CLASS = 'flex items-center gap-1 py-1 text-sm text-text hover:text-accent-text';

const FooterLinks = ({ links }: { links: FooterLink[] }) => (
  <>
    {links.map((l) =>
      l.external ? (
        <a key={l.label} href={l.to} className={LINK_CLASS}>
          {l.label}
          <Icon name="arrow-up-right" size={13} className="text-text-3" />
        </a>
      ) : (
        <Link key={l.label} to={l.to} className={LINK_CLASS}>
          {l.label}
        </Link>
      ),
    )}
  </>
);

const SocialRow = () => (
  <div className="flex items-center gap-4 text-text-3">
    <a
      href={DISCORD_URL}
      target="_blank"
      rel="noreferrer"
      aria-label={m.footer_social_label({ name: 'Discord' })}
      className="inline-flex hover:text-text"
    >
      <DiscordIcon size={18} />
    </a>
    {SOCIALS.map((s) => (
      <a
        key={s.label}
        href={s.href}
        target="_blank"
        rel="noreferrer"
        aria-label={m.footer_social_label({ name: s.label })}
        className="inline-flex hover:text-text"
      >
        <BrandGlyph id={s.brand} size={17} />
      </a>
    ))}
  </div>
);

/** Site footer. `slim` is a single-line variant for detail pages. */
export const Footer = ({ variant = 'full' }: FooterProps) => {
  if (variant === 'slim') {
    return (
      <footer className="border-t border-rule py-7.5 text-13 text-text-3">
        <div className="mx-auto flex flex-wrap justify-between gap-2.5 px-4 sm:px-8">
          <span>© 2026 AnyAPI Labs, Inc.</span>
          <span>{m.footer_slim_tagline()}</span>
        </div>
      </footer>
    );
  }

  return (
    <footer className="border-t border-rule pb-12 pt-16">
      <div className="mx-auto px-4 sm:px-8">
        <div className="grid grid-cols-2 gap-x-8 gap-y-10 s720:grid-cols-3 lg:grid-cols-4">
          {footerColumns().map((col, i) => (
            <div key={`${col.title}-${i}`} className="flex flex-col">
              <h2
                className={`mb-3 text-sm text-text-3 ${col.continuation ? 'opacity-0' : ''}`}
                aria-hidden={col.continuation}
              >
                {col.title}
              </h2>
              <FooterLinks links={col.links} />
            </div>
          ))}
        </div>
        <div className="mt-14 flex flex-wrap items-center justify-between gap-6 border-t border-rule pt-6">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-13 text-text-3">
            <Link to="/" className="flex items-center gap-2 text-15 font-bold tracking-[-0.02em]">
              <BrandMark size={19} />
              <span className="text-text">
                <span className="text-accent">any</span>api
              </span>
            </Link>
            <span>© 2026 AnyAPI Labs, Inc.</span>
            <LaunchBadges />
          </div>
          <SocialRow />
        </div>
      </div>
    </footer>
  );
};
